import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, MessageCircleQuestion } from "lucide-react";

export default function FAQ({ items, heading = "Frequently asked questions", intro }) {
  const [open, setOpen] = useState(0);

  return (
    <section className="pb-24 md:pb-32" data-testid="faq-section">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">
              <MessageCircleQuestion size={16} /> Questions & answers
            </span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] leading-tight">
              {heading}
            </h2>
            {intro && <p className="mt-5 text-lg text-[#334155]">{intro}</p>}
          </div>

          <div className="space-y-4">
            {items.map((item, i) => {
              const isOpen = open === i;
              return (
                <div
                  key={item.q}
                  className={`bg-white rounded-3xl border transition-all duration-300 ${isOpen ? "border-[#0EA5E9] shadow-lg" : "border-slate-100 shadow-sm"}`}
                  data-testid={`faq-item-${i}`}
                >
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 text-left px-6 md:px-8 py-6"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-heading text-lg font-semibold text-[#0F172A]">{item.q}</span>
                    <span className={`grid place-items-center w-9 h-9 rounded-full shrink-0 transition-colors ${isOpen ? "bg-[#0EA5E9] text-white" : "bg-[#0EA5E9]/10 text-[#0EA5E9]"}`}>
                      {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 md:px-8 pb-6 text-[#334155] leading-relaxed whitespace-pre-line">{item.a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          <p className="mt-10 text-center text-[#64748B]">
            Have a question that hasn't been answered?{" "}
            <a href="mailto:hello@happytiddlers.co.uk" className="font-semibold text-[#0EA5E9] hover:underline" data-testid="faq-email-link">
              Get in touch
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
